import type { CartPricingLineRequest } from '@repo/api/web/models'
import { CartPricingResponse } from '@repo/api/web/models'
import { debounce } from 'lodash-es'

import { usePromotionModule } from './usePromotionModule'

export type RefreshPricingOptions = {
  activePromoCode?: string | null
  trigger?: 'selection' | 'points' | 'manual'
}

export interface CartPricingLineInput {
  id?: number
  amount: number
}

const PRICING_DEBOUNCE_MS = 400

export const useCartPricing = (
  selectedItems: Ref<CartPricingLineInput[]>,
  options?: { immediate?: boolean },
) => {
  const { service, store } = usePromotionModule()
  const { support: authSupport } = useAuth()

  let requestId = 0

  function buildLines(): CartPricingLineRequest[] | null {
    const sourceItems = selectedItems.value
    const lines = sourceItems
      .filter(item => item.id)
      .map(item => ({
        productId: item.id!,
        qty: item.amount || 1,
      }))

    if (!sourceItems.length) {
      return []
    }

    if (!lines.length) {
      return null
    }

    return lines
  }

  function resolveActivePromoCode(options?: RefreshPricingOptions) {
    if (options && 'activePromoCode' in options) {
      return options.activePromoCode ?? null
    }

    return store.pricing?.activePromoCode ?? null
  }

  function applyEmptySelectionPricing(options?: RefreshPricingOptions) {
    const activePromoCode = resolveActivePromoCode(options)

    store.setPricingError(null)
    store.setPricing(
      activePromoCode
        ? CartPricingResponse.createLazy({ activePromoCode })
        : null,
    )
  }

  async function refreshPricing(options?: RefreshPricingOptions) {
    if (!authSupport.checkAuth()) {
      return
    }

    const lines = buildLines()

    if (!lines) {
      return
    }

    if (!lines.length) {
      if (options?.trigger === 'points') {
        return
      }

      requestId += 1
      applyEmptySelectionPricing(options)
      return
    }

    const currentRequestId = ++requestId
    store.setPricingError(null)
    store.setPricingLoading(true)

    try {
      const response = await service.pricing(lines, store.pointsToSpend)

      if (currentRequestId !== requestId) {
        return
      }

      store.setPricing(response)
    } catch (error) {
      if (currentRequestId !== requestId) {
        return
      }

      store.setPricingError(error)
    } finally {
      if (currentRequestId === requestId) {
        store.setPricingLoading(false)
      }
    }
  }

  async function syncPromoFromServer() {
    if (!authSupport.checkAuth()) {
      return
    }

    const currentRequestId = ++requestId
    store.setPricingError(null)
    store.setPricingLoading(true)

    try {
      const response = await service.pricing(null, store.pointsToSpend)

      if (currentRequestId !== requestId) {
        return
      }

      store.setPricing(response)
    } catch (error) {
      if (currentRequestId !== requestId) {
        return
      }

      store.setPricingError(error)
    } finally {
      if (currentRequestId === requestId) {
        store.setPricingLoading(false)
      }
    }
  }

  const debouncedRefresh = debounce(refreshPricing, PRICING_DEBOUNCE_MS)

  watch(
    selectedItems,
    () => {
      debouncedRefresh({ trigger: 'selection' })
    },
    { deep: true },
  )

  watch(
    () => store.pointsToSpend,
    () => {
      debouncedRefresh({ trigger: 'points' })
    },
  )

  if (options?.immediate !== false) {
    onMounted(() => {
      refreshPricing({ trigger: 'manual' })
    })
  }

  onBeforeUnmount(() => {
    debouncedRefresh.cancel()
    requestId += 1
  })

  const lineUnitPriceByProductId = computed(() => {
    const map = new Map<number, number>()
    const qtyByProduct = new Map<number, number>()

    for (const item of selectedItems.value) {
      if (!item.id) continue
      qtyByProduct.set(item.id, (qtyByProduct.get(item.id) ?? 0) + (item.amount || 1))
    }

    for (const line of store.pricing?.lines ?? []) {
      if (!line.productId) continue

      const qty = qtyByProduct.get(line.productId) ?? 1
      map.set(line.productId, (line.finalLineTotal ?? 0) / qty)
    }

    return map
  })

  return {
    refreshPricing,
    syncPromoFromServer,
    lineUnitPriceByProductId,
    pricing: computed(() => store.pricing),
    pricingLoading: computed(() => store.pricingLoading),
    pricingError: computed(() => store.pricingError),
    activePromoCode: computed(() => store.activePromoCode),
    pointsToSpend: computed({
      get: () => store.pointsToSpend,
      set: value => store.setPointsToSpend(value),
    }),
    canRedeemPoints: computed(() => store.canRedeemPoints),
  }
}
